import { extendTheme, type ThemeConfig } from '@chakra-ui/react'

/**
 * Altar brand theme.
 *
 * A faithful Chakra implementation of the approved brand design
 * (docs/brand/brand-design.md): a "carved stone" visual language with a
 * parchment light identity and an umber dark variant, Marcellus lapidary
 * headings over Inter body text, square corners, groove-rule boundaries,
 * recessed wells, and a flame-orange accent for primary actions.
 *
 * Every colour below is a semantic token so a single value flips between the
 * parchment (default) and umber (_dark) palettes. Components and pages should
 * reference these tokens (`bg.canvas`, `text.engraved`, `brand.accent`, …)
 * rather than raw Chakra greys.
 */

const config: ThemeConfig = {
  useSystemColorMode: true,
  initialColorMode: 'system',
}

/**
 * Semantic colour tokens. `default` is the parchment (light) identity; `_dark`
 * is the umber variant. Names map directly onto the palette tables in
 * docs/brand/brand-design.md.
 */
const semanticColors = {
  // Page and section surfaces — the alternating "strata" of carved stone.
  'bg.canvas': { default: '#f0e9d8', _dark: '#221c10' },
  'bg.band': { default: '#e7dec7', _dark: '#2e2616' },
  'bg.recess': { default: '#e0d6ba', _dark: '#1b160c' },

  // Type.
  'text.body': { default: '#3a3020', _dark: '#ece2cb' },
  'text.engraved': { default: '#544631', _dark: '#ece2cb' },
  'text.muted': { default: '#6f6149', _dark: '#b3a384' },

  // Brand colours.
  'brand.gold': { default: '#9a7c3f', _dark: '#b89a5e' },
  'brand.accent': { default: '#c05a18', _dark: '#e07a2f' },
  'brand.archline': { default: '#5a4a2f', _dark: '#c9b285' },

  // Groove rule — a two-tone incised line (dark half above, light half below).
  'line.dark': { default: '#d3c5a0', _dark: '#3e3420' },
  'line.light': { default: '#fffef6', _dark: '#4a3f28' },

  // The most sacred state keeps the green family for universal recognisability.
  'success.bg': { default: '#dde8d2', _dark: '#26331c' },
  'success.fg': { default: '#3f6f33', _dark: '#9cc78a' },

  // The engraved letterpress shadow — light cut on parchment, dark cut on umber.
  'shadow.engrave': { default: 'rgba(255,255,255,0.85)', _dark: 'rgba(0,0,0,0.55)' },

  // Drive Chakra's own built-in body/border tokens from the brand palette so
  // every surface across the product (not just the landing page) inherits
  // parchment/umber rather than the default grey.
  'chakra-body-bg': { default: '#f0e9d8', _dark: '#221c10' },
  'chakra-body-text': { default: '#3a3020', _dark: '#ece2cb' },
  'chakra-border-color': { default: '#d3c5a0', _dark: '#3e3420' },
  'chakra-placeholder-color': { default: '#6f6149', _dark: '#b3a384' },
  'chakra-subtle-bg': { default: '#e7dec7', _dark: '#2e2616' },
  'chakra-subtle-text': { default: '#6f6149', _dark: '#b3a384' },
}

/**
 * Named colour ramps registered so Chakra's `colorScheme` prop can resolve to
 * the brand palette. The semantic tokens above drive surfaces and type; these
 * ramps exist for the components that take a `colorScheme` (Badge, Tag, Switch,
 * Button) and would otherwise fall back to a generic Chakra hue. `flame` is the
 * burnt-orange accent (anchored on `brand.accent` at shade 500) and `gold` is
 * the lapidary gold (anchored on `brand.gold` at shade 500).
 */
const brandColorScales = {
  flame: {
    50: '#fbf0e7',
    100: '#f3d8c4',
    200: '#eaba98',
    300: '#e09a6b',
    400: '#d67d44',
    500: '#c05a18',
    600: '#a54c13',
    700: '#833c10',
    800: '#612c0c',
    900: '#3f1c07',
  },
  gold: {
    50: '#f8f3e6',
    100: '#ece0bf',
    200: '#dcc88f',
    300: '#cbb060',
    400: '#b8993f',
    500: '#9a7c3f',
    600: '#82682f',
    700: '#665021',
    800: '#4a3a17',
    900: '#2e240d',
  },
}

export const theme = extendTheme({
  config,
  colors: brandColorScales,
  fonts: {
    // Marcellus: Roman lapidary display face for the wordmark and headings.
    heading: `'Marcellus', 'Cormorant Garamond', Georgia, serif`,
    body: `'Inter', system-ui, Avenir, Helvetica, Arial, sans-serif`,
  },
  shadows: {
    // The keyboard focus ring. Chakra defaults this to blue, which clashes with
    // the warm palette; recolour it to a flame-accent ring used app-wide.
    outline: '0 0 0 3px rgba(192, 90, 24, 0.45)',
  },
  semanticTokens: {
    colors: semanticColors,
  },
  styles: {
    global: {
      'html, body': {
        backgroundColor: 'bg.canvas',
        color: 'text.body',
      },
      '#root': {
        backgroundColor: 'bg.canvas',
      },
      // The product carves its rules; remove the default rounded selection glow
      // in favour of a flame-accent text selection.
      '::selection': {
        backgroundColor: 'brand.accent',
        color: '#fffef6',
      },
    },
  },
  components: {
    Heading: {
      baseStyle: {
        fontFamily: `'Marcellus', 'Cormorant Garamond', Georgia, serif`,
        fontWeight: 400,
        color: 'text.engraved',
        letterSpacing: '0.01em',
        // The letterpress cut: a single-pixel highlight beneath the glyphs.
        textShadow: '0 1px 0 var(--chakra-colors-shadow-engrave)',
      },
    },
    Button: {
      baseStyle: {
        // Square corners — buttons are carved stone, not pills.
        borderRadius: 0,
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
      variants: {
        /** Primary action — the flame accent. */
        flame: {
          bg: 'brand.accent',
          color: '#fffef6',
          _hover: {
            bg: 'brand.accent',
            filter: 'brightness(1.08)',
            _disabled: { bg: 'brand.accent' },
          },
          _active: { filter: 'brightness(0.94)' },
        },
        /** Secondary action — a carved gold outline. */
        engrave: {
          bg: 'transparent',
          color: 'brand.gold',
          border: '1px solid',
          borderColor: 'brand.gold',
          _hover: { bg: 'bg.recess' },
          _active: { bg: 'bg.recess' },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: 0,
          backgroundColor: 'bg.band',
          color: 'text.body',
        },
      },
    },
    Input: {
      baseStyle: {
        field: { borderRadius: 0 },
      },
    },
    Textarea: {
      baseStyle: { borderRadius: 0 },
    },
    Select: {
      baseStyle: {
        field: { borderRadius: 0 },
      },
    },
    NumberInput: {
      baseStyle: {
        field: { borderRadius: 0 },
      },
    },
    Link: {
      baseStyle: {
        color: 'brand.accent',
        fontWeight: 500,
        _hover: { textDecoration: 'underline', color: 'brand.accent' },
      },
    },
    Divider: {
      baseStyle: {
        borderColor: 'line.dark',
        opacity: 1,
      },
    },
  },
})
